/**
 * Collision.
 *
 * The player is a vertical capsule (radius 0.5, half-height 1.0), which matches
 * v1's player AABB exactly at the equator. Horizontal resolution is therefore
 * circle-vs-rectangle in XZ, which rounds off v1's corners — the same feel
 * everywhere except that you no longer catch on a box edge you were not
 * touching.
 *
 * Two things v1 could not do, both of which a wagered brawler needs:
 *
 * - **Swept landing.** v1 tested `playerBottom >= platformTop - 0.5` against a
 *   single frame's position. At 30Hz a terminal-velocity fall covers 2 units
 *   per tick, so a 1-unit-thick platform is simply passed through. Landing is
 *   detected against the segment from the previous position to the current one.
 * - **Player-vs-player.** v1 never inserted remote players into collision at
 *   all (`remotePlayers` is referenced by eight files, none of them the physics
 *   engine), so bodies interpenetrated freely. Capsules push each other apart.
 */
import { DT, GROUND_SNAP, MAX_STEP_XZ, PLAYER_HALF_HEIGHT, PLAYER_RADIUS, PLAYER_SEPARATION_STRENGTH, } from "./constants.js";
export function aabbOverlap(a, b) {
    return (Math.abs(a.x - b.x) < a.hx + b.hx &&
        Math.abs(a.y - b.y) < a.hy + b.hy &&
        Math.abs(a.z - b.z) < a.hz + b.hz);
}
export function pointInAabb(x, y, z, b) {
    return (Math.abs(x - b.x) <= b.hx && Math.abs(y - b.y) <= b.hy && Math.abs(z - b.z) <= b.hz);
}
/** Squared distance from a point to the surface/interior of a box. Zero inside. */
export function pointAabbDist2(x, y, z, b) {
    const dx = Math.max(0, Math.abs(x - b.x) - b.hx);
    const dy = Math.max(0, Math.abs(y - b.y) - b.hy);
    const dz = Math.max(0, Math.abs(z - b.z) - b.hz);
    return dx * dx + dy * dy + dz * dz;
}
/**
 * Does a vertical capsule centred at (x, y, z) overlap the box?
 *
 * The capsule's spine runs from y - halfHeight to y + halfHeight; the test is
 * "closest point on the box to the spine is within `radius`", done separately
 * per axis because the box is axis-aligned.
 */
export function capsuleOverlapsAabb(x, y, z, radius, halfHeight, b) {
    if (Math.abs(y - b.y) > b.hy + halfHeight)
        return false;
    const dx = Math.max(0, Math.abs(x - b.x) - b.hx);
    const dz = Math.max(0, Math.abs(z - b.z) - b.hz);
    return dx * dx + dz * dz <= radius * radius;
}
/**
 * Resolves one player against one solid box, mutating position and velocity.
 *
 * `prevY` is the centre Y before this tick's integration; it is what makes the
 * landing test swept rather than instantaneous.
 */
export function resolveCapsuleVsBox(p, prevY, box, out, index) {
    if (resolveSweptWall(p, prevY, box))
        return;
    const top = box.y + box.hy;
    const bottom = box.y - box.hy;
    // Horizontal proximity first — cheap, and everything else depends on it.
    const nearestX = clampTo(p.x, box.x - box.hx, box.x + box.hx);
    const nearestZ = clampTo(p.z, box.z - box.hz, box.z + box.hz);
    const offX = p.x - nearestX;
    const offZ = p.z - nearestZ;
    const horizDist2 = offX * offX + offZ * offZ;
    if (horizDist2 > PLAYER_RADIUS * PLAYER_RADIUS)
        return;
    const feet = p.y - PLAYER_HALF_HEIGHT;
    const head = p.y + PLAYER_HALF_HEIGHT;
    const prevFeet = prevY - PLAYER_HALF_HEIGHT;
    const prevHead = prevY + PLAYER_HALF_HEIGHT;
    // Landing. Accepts both the "just barely below the lip" case v1 handled and
    // the "crossed the whole platform in one tick" case it did not.
    if (p.vy <= 0 && prevFeet >= top - GROUND_SNAP && feet <= top) {
        const candidateY = top + PLAYER_HALF_HEIGHT;
        if (!out.grounded || candidateY > out.groundY) {
            out.grounded = true;
            out.groundY = candidateY;
            out.groundIndex = index;
        }
        return;
    }
    // Ceiling. v1 had none, so you could pop up through any platform from below.
    // Gated on the centre actually being inside the footprint (the clamp returns
    // the centre itself there, so horizDist2 is exactly 0): within-a-radius of
    // the rim used to count too, so jumping BESIDE a floating slab hit a phantom
    // ceiling at bottom − halfHeight and died 0.4u up — on the graybox that was
    // every "stand near ledge, jump, climb it". A shoulder graze now falls
    // through to the horizontal push below, which nudges the riser sideways and
    // lets the jump keep its arc.
    if (p.vy > 0 && prevHead <= bottom && head >= bottom && horizDist2 === 0) {
        p.y = bottom - PLAYER_HALF_HEIGHT;
        p.vy = 0;
        out.ceiling = true;
        return;
    }
    // Genuine vertical overlap left: push out horizontally.
    if (head <= bottom || feet >= top)
        return;
    if (horizDist2 > 1e-12) {
        const dist = Math.sqrt(horizDist2);
        const push = PLAYER_RADIUS - dist;
        p.x += (offX / dist) * push;
        p.z += (offZ / dist) * push;
        // Kill only the component driving into the wall; sliding along it survives.
        const nx = offX / dist;
        const nz = offZ / dist;
        const into = p.vx * nx + p.vz * nz;
        if (into < 0) {
            p.vx -= nx * into;
            p.vz -= nz * into;
        }
        return;
    }
    // Centre is inside the footprint: eject along the shallowest axis, as v1 did.
    const overlapX = box.hx + PLAYER_RADIUS - Math.abs(p.x - box.x);
    const overlapZ = box.hz + PLAYER_RADIUS - Math.abs(p.z - box.z);
    if (overlapX < overlapZ) {
        p.x += p.x > box.x ? overlapX : -overlapX;
        p.vx = 0;
    }
    else {
        p.z += p.z > box.z ? overlapZ : -overlapZ;
        p.vz = 0;
    }
}
/**
 * Swept wall test, run before the endpoint resolution above.
 *
 * A dash covers 1.4u per tick (DASH_SPEED * DT) and a Volt sprint 1.08, so a
 * wall thinner than ~1.8u fits between two position samples: the endpoint
 * either cleared the far face entirely (no contact found at all) or landed past
 * the box centre, where the shallow-axis eject pushed the player out the FAR
 * side. Both read as walking through a solid wall.
 *
 * The tick's horizontal displacement is reconstructed from velocity — it is
 * exactly what integration applied (`clamp(v * DT)`), and nothing between
 * integration and collision moves a player horizontally — and the segment is
 * slab-tested against the box expanded by the capsule radius. Only an entry
 * from OUTSIDE counts (`tEntry >= 0`, and a capsule starting inside the
 * expanded box gets a negative entry time): resting contact and wall-slide
 * stay owned by the endpoint logic, whose exact-equality behaviour the rest of
 * the game leans on. `tEntry === 0` is deliberately IN: a player pinned
 * against the face, dashing straight at it, starts exactly on the boundary and
 * was one of the measured tunnels.
 *
 * On a hit only the entry axis is clamped; the other axis keeps its full
 * displacement and the tangential velocity survives, which is the same
 * kill-only-the-into-component contract the push-out uses — sliding along a
 * wall must feel identical whether you approached it fast or slow.
 */
function resolveSweptWall(p, prevY, box) {
    const dx = clampTo(p.vx * DT, -MAX_STEP_XZ, MAX_STEP_XZ);
    const dz = clampTo(p.vz * DT, -MAX_STEP_XZ, MAX_STEP_XZ);
    if (dx === 0 && dz === 0)
        return false;
    const prevX = p.x - dx;
    const prevZ = p.z - dz;
    const ex = box.hx + PLAYER_RADIUS;
    const ez = box.hz + PLAYER_RADIUS;
    const rx = prevX - box.x;
    const rz = prevZ - box.z;
    let tEntry = -Infinity;
    let tExit = Infinity;
    let entryOnX = false;
    if (dx === 0) {
        if (Math.abs(rx) >= ex)
            return false;
    }
    else {
        let t1 = (-ex - rx) / dx;
        let t2 = (ex - rx) / dx;
        if (t1 > t2) {
            const t = t1;
            t1 = t2;
            t2 = t;
        }
        tEntry = t1;
        entryOnX = true;
        tExit = t2;
    }
    if (dz === 0) {
        if (Math.abs(rz) >= ez)
            return false;
    }
    else {
        let t1 = (-ez - rz) / dz;
        let t2 = (ez - rz) / dz;
        if (t1 > t2) {
            const t = t1;
            t1 = t2;
            t2 = t;
        }
        if (t1 > tEntry) {
            tEntry = t1;
            entryOnX = false;
        }
        if (t2 < tExit)
            tExit = t2;
    }
    if (tEntry < 0 || tEntry > 1 || tEntry > tExit)
        return false;
    // Landing's own rule, applied at the moment of impact: feet within
    // GROUND_SNAP of the top mean this is a step onto the box, not a wall hit.
    // Between land() snaps a grounded player's feet sag ~0.09 below the surface
    // (one tick of gravity), so gating on the raw top would wall-stop everyone
    // walking across the seam of two flush platforms.
    const yAt = prevY + (p.y - prevY) * tEntry;
    const feetAt = yAt - PLAYER_HALF_HEIGHT;
    const headAt = yAt + PLAYER_HALF_HEIGHT;
    if (feetAt >= box.y + box.hy - GROUND_SNAP || headAt <= box.y - box.hy)
        return false;
    if (entryOnX) {
        p.x = prevX + dx * tEntry + (dx > 0 ? -SWEEP_SKIN : SWEEP_SKIN);
        if ((dx > 0 && p.vx > 0) || (dx < 0 && p.vx < 0))
            p.vx = 0;
    }
    else {
        p.z = prevZ + dz * tEntry + (dz > 0 ? -SWEEP_SKIN : SWEEP_SKIN);
        if ((dz > 0 && p.vz > 0) || (dz < 0 && p.vz < 0))
            p.vz = 0;
    }
    return true;
}
/** Separates two player capsules that have interpenetrated in XZ. */
export function separatePlayers(a, b) {
    // Only bodies overlapping vertically as well as horizontally collide, so
    // standing on someone's head is a stack rather than a shove.
    if (Math.abs(a.y - b.y) >= PLAYER_HALF_HEIGHT * 2)
        return;
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    const d2 = dx * dx + dz * dz;
    const minDist = PLAYER_RADIUS * 2;
    if (d2 >= minDist * minDist)
        return;
    if (d2 <= 1e-9) {
        // Exactly coincident. Break the tie on id so both runtimes pick the same
        // direction — a random nudge here is a divergence.
        const sign = a.id < b.id ? 1 : -1;
        a.x += sign * minDist * 0.5;
        b.x -= sign * minDist * 0.5;
        return;
    }
    const dist = Math.sqrt(d2);
    const push = ((minDist - dist) * PLAYER_SEPARATION_STRENGTH) / 2;
    const nx = dx / dist;
    const nz = dz / dist;
    a.x += nx * push;
    a.z += nz * push;
    b.x -= nx * push;
    b.z -= nz * push;
}
/**
 * How much shorter the swept capsule is than the real one.
 *
 * Big enough to clear the floating-point equality of resting contact, small
 * enough that it cannot let a dash pass through a real wall: at 0.02 units a
 * blocking box would have to be under 2cm tall to be missed, and the thinnest
 * platform in any map is 1 unit.
 */
const SWEEP_SKIN = 0.02;
/**
 * Sweeps a point from (x,z,y) along a direction, stopping short of the first
 * solid box. Used by teleporting abilities — Blink Strike must not put a player
 * inside a wall.
 */
export function sweepPoint(x, y, z, dirX, dirZ, distance, boxes, step = 0.5) {
    let travelled = 0;
    let cx = x;
    let cz = z;
    while (travelled < distance) {
        const advance = Math.min(step, distance - travelled);
        const nx = cx + dirX * advance;
        const nz = cz + dirZ * advance;
        let blocked = false;
        for (const b of boxes) {
            if (!b.solid)
                continue;
            // Shrunk capsule, and this is the whole ability working or not.
            //
            // A player standing on a platform sits at exactly
            // `plat.y + plat.hy + halfHeight`, so `|y - b.y|` EQUALS `b.hy +
            // halfHeight` — and `capsuleOverlapsAabb` tests `>`, so resting contact
            // counts as an overlap. The sweep was therefore blocked at its very first
            // probe by the floor under the caster's own feet: Blink Strike moved you
            // 0m every time you used it on the ground, which is every time.
            //
            // Trimmed here rather than in `capsuleOverlapsAabb`, because collision
            // RESOLUTION depends on resting contact registering — loosening it there
            // drops players through floors.
            if (capsuleOverlapsAabb(nx, y, nz, PLAYER_RADIUS, PLAYER_HALF_HEIGHT - SWEEP_SKIN, b)) {
                blocked = true;
                break;
            }
        }
        if (blocked)
            break;
        cx = nx;
        cz = nz;
        travelled += advance;
    }
    return { x: cx, z: cz, travelled };
}
function clampTo(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
//# sourceMappingURL=collision.js.map